import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  FormDialogMode,
  RowCustomAction,
  useNotify
} from '@globaleris/ui.library'
import { makeStyles, useTheme } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import { PricingTableModel, TabsPriceType } from '../../pricing.Model'

import {
  getActiveCompany,
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../../redux/helpers'

import { AccountTree, Delete, Edit, Pageview } from '@material-ui/icons'
import { EnumCommodity, EnumPrecioUnitario } from '../../../../api'
import { envConfig } from '../../../../config/envConfig'
import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import { useConfirmationDialog } from '../../../../hooks'
import { useHttpError } from '../../../../hooks/useHttpError'
import { NonAuthRoutes } from '../../../../model'
import { Pages } from '../../../../model/RBAC/constants'
import { DataTableFilter } from '../../../../redux'
import {
  deletePriceComponentType,
  fetchPricingListItems,
  fetchPricingListItemsNotSubscribed,
  fetchPricingParametersBasePriceComponentsListItems,
  fetchPricingParametersListItems
} from '../../pricing.service'

import { EnumPriceComponentReference } from '../../../../api/Argentia/enums'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { enumKeyToValue, enumToSelectOptionArray } from '../../../../helpers'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { ServiceResponse } from '../../../../model/serviceResponse'
import { PriceComponentTypeForm } from '../../priceComponentType/components/priceComponentTypeForm'
import { styles } from './pricingTable.styles'

export interface PricingTableProps {
  commodity: EnumCommodity
  priceType: EnumPriceType
  tabType: TabsPriceType
  dataTableFilter: DataTableFilter | undefined
  dataTableSearch: string | undefined
  onFetching: (isFetching: boolean) => void
  isTabAllowedForCommodity: (commodity: EnumCommodity, tabValue: number) => boolean
}

export const PricingTable: React.FC<PricingTableProps> = props => {
  const [t] = useTranslation(['pricing', 'enums'])

  const activeCompany = getActiveCompany()

  const [prices, setPrices] = useState<PricingTableModel[] | undefined>(undefined)
  const [rowData, setRowData] = useState<PricingTableModel | undefined>(undefined)
  const [openPriceComponentTypeEditDialog, setOpenPriceComponentTypeEditDialog] = useState(false)

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const history = useHistory()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { showError, clearError, errorProps } = useHttpError()
  const { info, error } = useNotify()

  const tabIsBaseComponents = props.tabType === TabsPriceType.baseComponents
  const tabIsCotization = props.tabType === TabsPriceType.cotization
  const tabIsParameterPeriod = props.tabType === TabsPriceType.parameterPeriod
  const tabIsApuntamiento = props.tabType === TabsPriceType.apuntamiento

  // Handlers Starts

  const handleOnClickAdd = async () => {
    switch (props.tabType) {
      case TabsPriceType.baseComponents:
        history.push(NonAuthRoutes.priceComponentTypeAdd, {
          commodity: props.commodity
        })
        return true
      default:
        history.push(NonAuthRoutes.pricingAdd, {
          commodity: props.commodity,
          priceType: props.priceType,
          pricingTab: props.tabType
        })
        return true
    }
  }

  const handleFormDialogClose = () => {
    setOpenPriceComponentTypeEditDialog(false)
  }

  const handleViewPriceDetails = (row: PricingTableModel) => {
    const priceId = definePriceId(row)
    const priceType = definePriceType(row)

    if (priceId) {
      history.push(NonAuthRoutes.pricingEdit, {
        priceId: priceId,
        commodity: props.commodity,
        priceType: priceType,
        tabType: props.tabType
      })
    } else {
      error(t('pricing:globalNotifications.error.priceIdError'))
    }
  }

  const handleDeletePriceComponentType = async (row: PricingTableModel) => {
    await getConfirmation(ConfirmationDialogs.DELETE_PRICE_COMPONENT_TYPE).then(async () => {
      await deletePriceComponentType(row.idPriceComponentType!).then(response => {
        if (response.success) {
          info(t('pricing:globalNotifications.success.priceComponentTypeDeleted'))
          fetchPricesList(props.commodity, props.tabType)
        } else {
          error(t('pricing:globalNotifications.error.priceComponentTypeDeleteError'))
        }
      })
      closeConfirmation()
    })
  }

  // Handlers Ends

  // DataTable Config Starts

  const columns: DataTableColumn[] = [
    {
      name: 'priceComponentTypeCode',
      label: t('pricing:dataTable.columns.priceComponentTypeCode'),
      type: DataType.TEXT,
      display: tabIsBaseComponents
    },
    {
      name: 'priceComponentTypeDescription',
      label: t('pricing:dataTable.columns.priceComponentTypeDescription'),
      type: DataType.TEXT,
      display: tabIsBaseComponents
    },
    {
      name: 'code',
      label: t('pricing:dataTable.columns.priceCode'),
      type: DataType.TEXT,
      display: !tabIsBaseComponents
    },
    {
      name: 'description',
      label: t('pricing:dataTable.columns.description'),
      type: DataType.TEXT,
      columnWidth: 400,
      display: !tabIsBaseComponents
    },
    {
      name: 'precioUnitario',
      label: t('pricing:dataTable.columns.priceUnit'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit').map(
        option => {
          return { ...option, value: EnumPrecioUnitario[option.value] }
        }
      ),
      columnWidth: 10
    },
    {
      name: 'priceComponentTypeReference',
      label: t('pricing:dataTable.columns.priceComponentTypeReference'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumPriceComponentReference,
        'enums:EnumPriceComponentReference'
      ),
      display: tabIsBaseComponents
    },
    {
      name: 'priceType',
      label: t('pricing:dataTable.columns.priceType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumPriceType, 'enums:EnumPriceTypes'),
      display: tabIsBaseComponents
    },
    {
      name: 'marketType',
      label: t('pricing:dataTable.columns.productType'),
      type: DataType.TEXT,
      display: tabIsCotization,
      columnWidth: 10
    },
    {
      name: 'startDate',
      label: t('pricing:dataTable.columns.startDate'),
      type: DataType.DATE,
      columnWidth: 5,
      display: !tabIsBaseComponents
    },
    {
      name: 'endDate',
      label: t('pricing:dataTable.columns.endDate'),
      type: DataType.DATE,
      columnWidth: 5,
      display: !tabIsBaseComponents
    },
    {
      name: 'relatedPriceComponentCount',
      label: t('pricing:dataTable.columns.relatedPriceComponentCount'),
      type: DataType.NUMBER,
      customDecimalScale: 0,
      display: tabIsBaseComponents
    }
  ]

  const getRowCustomActions = () => {
    let rowCustomActions: RowCustomAction[] = []

    if (!tabIsBaseComponents) {
      rowCustomActions.push({
        icon: <Pageview fontSize="small" />,
        tooltip: t('pricing:dataTable.tooltips.view'),
        onClick: handleViewPriceDetails
      })
    }

    if (!tabIsParameterPeriod && !tabIsApuntamiento) {
      rowCustomActions.push(
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('pricing:dataTable.tooltips.edit'),
          onClick: (row: PricingTableModel) => {
            setOpenPriceComponentTypeEditDialog(true)
            setRowData(row)
          },
          visibleIf: 'canEditPriceComponentType'
        },
        {
          icon: <AccountTree fontSize="small" />,
          tooltip: t('pricing:dataTable.tooltips.instances'),
          onClick: (row: PricingTableModel) => {
            const exceedsMultipleInstances =
              (row.relatedPriceComponentCount || 0) >=
              envConfig.priceComponentInstancesConfig.threshold

            history.push(NonAuthRoutes.priceComponentTypeInstances, {
              pricingTableRowSelected: row,
              commodity: props.commodity,
              exceedsMultipleInstances
            })
          },
          visibleIf: 'canViewPriceComponentTypeInstances'
        }
      )
    }

    if (tabIsBaseComponents) {
      rowCustomActions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t('pricing:dataTable.tooltips.delete'),
        onClick: handleDeletePriceComponentType
      })
    }
    return rowCustomActions
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    filterType: FilterType.ADVANCED,
    download: true,
    print: false,
    selectableRows: false,
    canDelete: false,
    canEdit: false,
    customInsertEditModal: true,
    fabLabel: t('pricing:dataTable.fab'),
    canInsert: true,
    onRowInsert: handleOnClickAdd,
    rowCustomActions: getRowCustomActions(),
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  // DataTable Config Ends

  // Functions Starts

  const definePriceId = (row: PricingTableModel) => {
    if (row.priceComponentTypeReference) {
      if (
        row.priceComponentTypeReference === EnumPriceComponentReference.Oferta ||
        row.priceComponentTypeReference === EnumPriceComponentReference.Lead
      ) {
        return row.idDummyPrice
      }
    }
    return row.id
  }

  const definePriceType = (row: PricingTableModel) => {
    if (row.priceComponentTypeReference) {
      if (
        row.priceComponentTypeReference === EnumPriceComponentReference.Oferta ||
        row.priceComponentTypeReference === EnumPriceComponentReference.Lead
      ) {
        return EnumPriceType.Dummy
      }
    }
    // Si row.priceType es una cadena, usar como clave del enum
    if (typeof row.priceType === 'string') {
      return enumKeyToValue(EnumPriceType, row.priceType)
    } else {
      return row.priceType
    }
  }

  const fetchPricesList = async (commodity: EnumCommodity, tab: TabsPriceType) => {
    props.onFetching(true)
    setPrices(undefined)

    let fetch: Promise<ServiceResponse<any>>
    switch (tab) {
      case TabsPriceType.parameter: {
        fetch = fetchPricingParametersListItems(commodity)
        break
      }
      case TabsPriceType.apuntamiento:
      case TabsPriceType.parameterPeriod: {
        fetch = fetchPricingListItemsNotSubscribed(commodity, props.priceType)
        break
      }
      case TabsPriceType.baseComponents: {
        fetch = fetchPricingParametersBasePriceComponentsListItems(commodity)
        break
      }
      default: {
        fetch = fetchPricingListItems(commodity, props.priceType)
        break
      }
    }

    await fetch.then(response => {
      props.onFetching(false)
      if (response.success) {
        clearError()
        setPrices(sortByCode(response.data, tab))
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => {
              fetchPricesList(commodity, props.tabType)
            }
          })
      }
    })
  }

  const sortByCode = (valuesArray: PricingTableModel[] | undefined = [], tab: TabsPriceType) => {
    let sortedArray: PricingTableModel[] = []

    if (tab === TabsPriceType.baseComponents) {
      sortedArray = valuesArray.sort((a, b) => {
        const codeA = a.priceComponentTypeCode || ''
        const codeB = b.priceComponentTypeCode || ''

        return codeA.localeCompare(codeB, 'es', { sensitivity: 'base' })
      })
    } else {
      sortedArray = valuesArray.sort((a, b) => {
        const codeA = a.code || '' // Si a.code es undefined, ya que ParametersBase no tiene esa propiedad
        const codeB = b.code || '' // Si b.code es undefined, ya que ParametersBase no tiene esa propiedad

        return codeA.localeCompare(codeB, 'es', { sensitivity: 'base' })
      })
    }

    return sortedArray
  }

  // Functions Ends

  // Hooks Starts

  useEffect(() => {
    if (!props.isTabAllowedForCommodity(props.commodity, props.tabType)) return

    fetchPricesList(props.commodity, props.tabType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.priceType, activeCompany, props.commodity])

  // Hooks Ends

  return prices || errorProps ? (
    <>
      <DataTable
        // className={classes.dataTable}
        data={prices}
        columns={columns}
        options={{
          ...options,
          analytics: {
            customer: getCustomerDomain(),
            view: t('pricing:dataTable.analytics')
          }
        }}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Pricing)}
        appliedFilter={props.dataTableFilter}
        appliedSearch={props.dataTableSearch}
      />
      {openPriceComponentTypeEditDialog && (
        <PriceComponentTypeForm
          formMode={FormDialogMode.EDIT}
          priceComponentTypeData={rowData}
          commodity={props.commodity}
          onClose={handleFormDialogClose}
          updateTableData={() => fetchPricesList(props.commodity, props.tabType)}
        />
      )}
    </>
  ) : (
    <DataTableSkeleton />
  )
}
